import axios from 'axios';
import { getCookie, setCookie } from '../utils/cookies';

// FAIL FAST: URL for Go API via runtime configuration
const getGoApiUrl = () => {
  // Special case for Electron app
  if (window.QUER_CONFIG && window.QUER_CONFIG.apiURL) {
    return window.QUER_CONFIG.apiURL;
  }

  // FAIL FAST: Configuration must exist and be valid
  if (!window.CONFIG) {
    throw new Error('FATAL: window.CONFIG is not defined. Cannot determine Go API URL.');
  }

  if (window.CONFIG.baseUrl === undefined || window.CONFIG.baseUrl === null) {
    throw new Error('FATAL: window.CONFIG.baseUrl is not defined. Cannot construct API URL.');
  }

  if (!window.CONFIG.apiEndpoints) {
    throw new Error('FATAL: window.CONFIG.apiEndpoints is not defined. Cannot determine API endpoints.');
  }

  if (!window.CONFIG.apiEndpoints.go) {
    throw new Error('FATAL: window.CONFIG.apiEndpoints.go is not defined. Cannot determine Go API endpoint.');
  }

  // Configuration is valid, construct URL
  // Handle empty string baseUrl for development environment (creates relative URL)
  const baseUrl = window.CONFIG.baseUrl || '';
  const apiUrl = baseUrl + window.CONFIG.apiEndpoints.go;
  console.log('[GoAPI] Using API URL from configuration:', apiUrl);

  return apiUrl;
};

// Remove static calculation, will calculate dynamically
// const GO_API_URL = getGoApiUrl();

// Class for working with Go API
export class GoAuthApi {
  // Caching to prevent multiple requests
  static cachedClerkToken = null;
  static tokenRequestCount = 0;
  static MAX_REQUESTS = 5;

  // Method to get token from cookie using utilities and caching
  static async getAuthToken() {
    // Already authenticated globally, return cache
    if (window.__GLOBAL_AUTH_COMPLETED && this.cachedClerkToken) {
      // Check if token has expired
      if (this.isTokenValid(this.cachedClerkToken)) {
        return this.cachedClerkToken;
      } else {
        console.log('[GoAPI] Cached token expired, getting new one');
        this.cachedClerkToken = null;
        window.__GLOBAL_CLERK_TOKEN_CACHE = null;
      }
    }
    
    // Protection against cyclic requests
    this.tokenRequestCount++;
    if (this.tokenRequestCount > this.MAX_REQUESTS) {
      console.log(`[GoAPI] Token request limit (${this.tokenRequestCount}/${this.MAX_REQUESTS}) reached, using cached token`);
      return this.cachedClerkToken;
    }
    
    // Check global token cache if set
    if (window.__GLOBAL_CLERK_TOKEN_CACHE) {
      // Check validity of global cache
      if (this.isTokenValid(window.__GLOBAL_CLERK_TOKEN_CACHE)) {
        console.log('[GoAPI] Using global token cache');
        this.cachedClerkToken = window.__GLOBAL_CLERK_TOKEN_CACHE;
        return window.__GLOBAL_CLERK_TOKEN_CACHE;
      } else {
        console.log('[GoAPI] Global token cache expired, clearing');
        window.__GLOBAL_CLERK_TOKEN_CACHE = null;
      }
    }
    
    // Check localStorage
    if (localStorage.getItem('__session')) {
      const localStorageToken = localStorage.getItem('__session');
      if (this.isTokenValid(localStorageToken)) {
        console.log('[GoAPI] Using token from localStorage');
        this.cachedClerkToken = localStorageToken;
        window.__GLOBAL_CLERK_TOKEN_CACHE = localStorageToken;
        return localStorageToken;
      } else {
        console.log('[GoAPI] Token in localStorage expired, clearing');
        localStorage.removeItem('__session');
      }
    }
    
    // FIX: Add token check from Clerk directly
    if (window.Clerk && window.Clerk.session) {
      try {
        console.log('[GoAPI] Attempting to get fresh token from Clerk session');
        const clerkToken = await window.Clerk.session.getToken();
        if (clerkToken && this.isTokenValid(clerkToken)) {
          console.log('[GoAPI] Got fresh valid token from Clerk');
          this.cachedClerkToken = clerkToken;
          window.__GLOBAL_CLERK_TOKEN_CACHE = clerkToken;
          localStorage.setItem('__session', clerkToken);
          return clerkToken;
        }
      } catch (clerkError) {
        console.warn('[GoAPI] Error getting token from Clerk session:', clerkError);
      }
    }
    
    if (window.electronAPI) {
      // Set global request flag to prevent cycles
      if (window.__TOKEN_REQUEST_IN_PROGRESS) {
        console.log('[GoAPI] Token request already in progress, waiting');
        await new Promise(resolve => setTimeout(resolve, 100)); // Small delay
        return this.cachedClerkToken || window.__GLOBAL_CLERK_TOKEN_CACHE;
      }
      
      try {
        window.__TOKEN_REQUEST_IN_PROGRESS = true;
        
        // In version 1.5: Prioritize getting Clerk Session Token
        if (window.electronAPI.getClerkSessionToken) {
          try {
            const clerkToken = await window.electronAPI.getClerkSessionToken();
            if (clerkToken && this.isTokenValid(clerkToken)) {
              console.log('[GoAPI] Using valid Clerk Session Token from Electron store');
              // Cache to prevent repeated requests
              this.cachedClerkToken = clerkToken;
              window.__GLOBAL_CLERK_TOKEN_CACHE = clerkToken;
              window.__TOKEN_REQUEST_IN_PROGRESS = false;
              return clerkToken;
            } else if (clerkToken) {
              console.log('[GoAPI] Clerk token from Electron store expired, getting fresh one');

              // First try to refresh token if expiration time is near
              try {
                const payload = JSON.parse(atob(clerkToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
                const timeToExpiry = payload.exp - Math.floor(Date.now() / 1000);
                
                console.log(`[GoAPI] Time to token expiry: ${timeToExpiry} seconds`);
                
                // If more than 30 seconds remaining, try to use the token
                if (timeToExpiry > 30) {
                  console.log('[GoAPI] Token still has significant time left, using it');
                  this.cachedClerkToken = clerkToken;
                  window.__GLOBAL_CLERK_TOKEN_CACHE = clerkToken;
                  window.__TOKEN_REQUEST_IN_PROGRESS = false;
                  return clerkToken;
                }
              } catch (parseError) {
                console.error('[GoAPI] Error parsing token for expiry check:', parseError);
              }
              
              // Send request to refresh token via Clerk
              if (window.Clerk && window.Clerk.session) {
                try {
                  // FIX: Use correct getToken() method
                  const freshToken = await window.Clerk.session.getToken();
                  if (freshToken && this.isTokenValid(freshToken)) {
                    // Save new token
                    await window.electronAPI.storeClerkSessionToken(freshToken);
                    this.cachedClerkToken = freshToken;
                    window.__GLOBAL_CLERK_TOKEN_CACHE = freshToken;
                    window.__TOKEN_REQUEST_IN_PROGRESS = false;
                    console.log('[GoAPI] Got fresh token from Clerk session');
                    return freshToken;
                  }
                } catch (err) {
                  console.error('[GoAPI] Error getting fresh token from Clerk:', err);
                  // If cannot refresh, try through window.__clerk
                  if (window.__clerk && window.__clerk.session) {
                    try {
                      const freshToken = await window.__clerk.session.getToken();
                      if (freshToken && this.isTokenValid(freshToken)) {
                        await window.electronAPI.storeClerkSessionToken(freshToken);
                        this.cachedClerkToken = freshToken;
                        window.__GLOBAL_CLERK_TOKEN_CACHE = freshToken;
                        window.__TOKEN_REQUEST_IN_PROGRESS = false;
                        console.log('[GoAPI] Got fresh token from __clerk session');
                        return freshToken;
                      }
                    } catch (err2) {
                      console.error('[GoAPI] Error getting token from __clerk:', err2);
                    }
                  }
                }
              }
            }
          } catch (error) {
            console.error('[GoAPI] Error getting Clerk token:', error);
            // Return cache on error
            window.__TOKEN_REQUEST_IN_PROGRESS = false;
            if (this.cachedClerkToken) {
              return this.cachedClerkToken;
            }
          }
        }
        
        // Fallback for backward compatibility
        try {
          const token = await window.electronAPI.getAuthToken();
          if (token && this.isTokenValid(token)) {
            this.cachedClerkToken = token;
            window.__GLOBAL_CLERK_TOKEN_CACHE = token;
          }
          window.__TOKEN_REQUEST_IN_PROGRESS = false;
          return token;
        } catch (error) {
          console.error('[GoAPI] Error getting auth token:', error);
          window.__TOKEN_REQUEST_IN_PROGRESS = false;
          return this.cachedClerkToken;
        }
      } finally {
        // Guaranteed flag reset
        setTimeout(() => {
          window.__TOKEN_REQUEST_IN_PROGRESS = false;
        }, 500);
      }
    } else {
      // Get token only from cookie
      const clerkSessionCookie = getCookie('__session');
      if (clerkSessionCookie && this.isTokenValid(clerkSessionCookie)) {
        this.cachedClerkToken = clerkSessionCookie;
        window.__GLOBAL_CLERK_TOKEN_CACHE = clerkSessionCookie;
        return clerkSessionCookie;
      }
      
      const cookieToken = getCookie('auth_token');
      if (cookieToken && this.isTokenValid(cookieToken)) {
        this.cachedClerkToken = cookieToken;
        window.__GLOBAL_CLERK_TOKEN_CACHE = cookieToken;
      }
      return cookieToken || this.cachedClerkToken || null;
    }
  }

  // Method to check JWT token validity
  static isTokenValid(token) {
    if (!token) return false;

    try {
      // Extract payload from JWT
      const base64Url = token.split('.')[1];
      if (!base64Url) return false;
      
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => 
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join(''));
      
      const payload = JSON.parse(jsonPayload);
      
      // Check token expiration time
      if (payload.exp) {
        const now = Math.floor(Date.now() / 1000);
        const bufferTime = 15; // OPTIMIZATION: Reduced buffer to 15 seconds (was 60) for less aggressive checking

        if (payload.exp <= (now + bufferTime)) {
          console.log(`[GoAPI] Token expires at ${new Date(payload.exp * 1000)}, current time ${new Date()}, buffer: ${bufferTime}s`);
          return false; // Token expired or will expire in the next 15 seconds
        }

        return true;
      }

      return false; // Token without expiration is considered invalid
    } catch (error) {
      console.error('[GoAPI] Error checking token validity:', error);
      return false;
    }
  }

  // Method for forced token refresh
  static async refreshToken() {
    console.log('[GoAPI] Refreshing token...');

    // Clear cache
    this.cachedClerkToken = null;
    window.__GLOBAL_CLERK_TOKEN_CACHE = null;
    this.tokenRequestCount = 0;

    // FIX: Also clear subscription cache when refreshing token
    this.cachedSubscriptionStatus = { isActive: false, isTrialing: false };
    this.lastSubscriptionCheck = 0;
    this.subscriptionChecks = 0;
    console.log('[GoAPI] Subscription cache cleared due to token refresh');

    // If Clerk is available, try to get new token
    if (window.Clerk && window.Clerk.session) {
      try {
        const freshToken = await window.Clerk.session.getToken();
        if (freshToken) {
          console.log('[GoAPI] Successfully refreshed token from Clerk session');

          // Save new token
          if (window.electronAPI && window.electronAPI.storeClerkSessionToken) {
            await window.electronAPI.storeClerkSessionToken(freshToken);
          }

          // Update localStorage
          localStorage.setItem('__session', freshToken);

          // Update cache
          this.cachedClerkToken = freshToken;
          window.__GLOBAL_CLERK_TOKEN_CACHE = freshToken;
          
          return freshToken;
        }
      } catch (error) {
        console.error('[GoAPI] Error refreshing token from Clerk session:', error);

        // Try through window.__clerk
        if (window.__clerk && window.__clerk.session) {
          try {
            const freshToken = await window.__clerk.session.getToken();
            if (freshToken) {
              console.log('[GoAPI] Successfully refreshed token from __clerk session');

              // Save new token
              if (window.electronAPI && window.electronAPI.storeClerkSessionToken) {
                await window.electronAPI.storeClerkSessionToken(freshToken);
              }

              // Update localStorage
              localStorage.setItem('__session', freshToken);

              // Update cache
              this.cachedClerkToken = freshToken;
              window.__GLOBAL_CLERK_TOKEN_CACHE = freshToken;
              
              return freshToken;
            }
          } catch (error2) {
            console.error('[GoAPI] Error refreshing token from __clerk session:', error2);
          }
        }
      }
    }
    
    // Try to get new token the usual way
    return await this.getAuthToken();
  }

  // Method to save token using utilities
  static async setAuthToken(token) {
    if (window.electronAPI) {
      // In version 1.5: Save Clerk Session Token directly
      if (window.electronAPI.storeClerkSessionToken) {
        console.log('[GoAPI] Storing token as Clerk Session Token');
        await window.electronAPI.storeClerkSessionToken(token);
      } else {
        // Fallback for backward compatibility
        await window.electronAPI.storeAuthToken(token);
      }
    } else {
      // Set token in cookie with secure parameters
      setCookie('auth_token', token, {
        path: '/',
        secure: true,
        httpOnly: true,
        sameSite: 'strict',
        maxAge: 3600 // 1 час
      });
    }
  }

  // Session verification with cookie support
  static async verifySession(token) {
    try {
      console.log('Verifying session status');
      // Standard timeout for all environments
      const timeoutMs = 10000;
      
      const headers = {};
      // Add token to headers only if explicitly specified
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.post(`${getGoApiUrl()}/auth/verify-session`, {}, {
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        withCredentials: true, // Important for reading and sending cookie
        timeout: timeoutMs
      });
      console.log('Session verification completed');
      return response.data;
    } catch (error) {
      console.error('Session verification failed');
      // Log only necessary details without sensitive information
      if (error.response) {
        console.error('Error status:', error.response.status);
      } else if (error.request) {
        console.error('No response received from server');
      }
      throw error;
    }
  }

  /* COMMENTED OUT IN VERSION 1.5: No longer needed as React app
  uses Clerk Session Token directly

  // Get API token from Go backend and set in secure cookie
  static async getToken(sessionToken) {
    try {
      console.log('[GoAuthApi] Requesting authentication token');
      
      // Remove development mode fallback for security
      if (!sessionToken) {
        throw new Error('Authentication required');
      }
      
      // Add debug information
      console.log('[GoAuthApi] Token length:', sessionToken.length);
      console.log('[GoAuthApi] Token prefix:', sessionToken.substring(0, 10) + '...');

      // Check cookies before request
      const cookiesBefore = document.cookie.split(';')
        .map(cookie => cookie.trim())
        .filter(cookie => cookie.length > 0);
      console.log('[GoAuthApi] Cookies before request:', cookiesBefore);

      // For debugging log all request headers
      const headers = {
        'Authorization': `Bearer ${sessionToken}`,
        'Content-Type': 'application/json'
      };
      console.log('[GoAuthApi] Request headers:', headers);
      console.log('[GoAuthApi] Request URL:', `${getGoApiUrl()}/auth/token`);
      console.log('[GoAuthApi] Request method: POST');
      console.log('[GoAuthApi] withCredentials: true');
      
      const response = await axios.post(`${getGoApiUrl()}/auth/token`, {}, {
        headers,
        timeout: 8000,
        withCredentials: true // Important for working with cookie
      });

      // Check cookies after request
      const cookiesAfter = document.cookie.split(';')
        .map(cookie => cookie.trim())
        .filter(cookie => cookie.length > 0);
      console.log('[GoAuthApi] Cookies after request:', cookiesAfter);

      // Check if new cookies were added
      const newCookies = cookiesAfter.filter(cookie => !cookiesBefore.includes(cookie));
      console.log('[GoAuthApi] New cookies after request:', newCookies);
      
      console.log('[GoAuthApi] Token request completed with status:', response.status);
      console.log('[GoAuthApi] Response data:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('[GoAuthApi] Token generation failed:', error);

      // Log only necessary info without sensitive information
      if (error.response) {
        console.error('[GoAuthApi] Error status:', error.response.status);
        console.error('[GoAuthApi] Error data:', error.response.data);
        console.error('[GoAuthApi] Error headers:', error.response.headers);
      } else if (error.request) {
        console.error('[GoAuthApi] No response received from server. Request:', error.request);
      } else {
        console.error('[GoAuthApi] Error message:', error.message);
      }
      
      // For debugging check cookies after error
      const cookiesAfterError = document.cookie.split(';')
        .map(cookie => cookie.trim())
        .filter(cookie => cookie.length > 0);
      console.log('[GoAuthApi] Cookies after error:', cookiesAfterError);
      
      throw error;
    }
  }
  
  // Secure session cookie setup through server API
  static async setSessionCookie(token) {
    try {
      console.log('[GoAuthApi] Setting up session authentication');
      console.log('[GoAuthApi] Token length:', token.length);
      console.log('[GoAuthApi] Token prefix:', token.substring(0, 10) + '...');
      
      // Check cookies before request
      const cookiesBefore = document.cookie.split(';')
        .map(cookie => cookie.trim())
        .filter(cookie => cookie.length > 0);
      console.log('[GoAuthApi] Cookies before request:', cookiesBefore);

      // Log request headers
      const headers = {
        'Content-Type': 'application/json'
      };
      console.log('[GoAuthApi] Request headers:', headers);
      console.log('[GoAuthApi] Request URL:', `${getGoApiUrl()}/auth/set-session-cookie`);
      console.log('[GoAuthApi] Request method: POST');
      console.log('[GoAuthApi] Request body:', { token: token.substring(0, 10) + '...' });
      
      const response = await axios.post(`${getGoApiUrl()}/auth/set-session-cookie`, { token }, {
        headers,
        withCredentials: true // Important for working with cookie
      });

      // Check cookies after request
      const cookiesAfter = document.cookie.split(';')
        .map(cookie => cookie.trim())
        .filter(cookie => cookie.length > 0);
      console.log('[GoAuthApi] Cookies after request:', cookiesAfter);

      // Check if new cookies were added
      const newCookies = cookiesAfter.filter(cookie => !cookiesBefore.includes(cookie));
      console.log('[GoAuthApi] New cookies after request:', newCookies);

      // Log response
      console.log('[GoAuthApi] Response status:', response.status);
      console.log('[GoAuthApi] Response data:', response.data);
      
      console.log('[GoAuthApi] Session setup completed successfully');
      return response.data;
    } catch (error) {
      console.error('[GoAuthApi] Session authentication setup failed');
      
      if (error.response) {
        console.error('[GoAuthApi] Error status:', error.response.status);
        console.error('[GoAuthApi] Error data:', error.response.data);
      } else if (error.request) {
        console.error('[GoAuthApi] No response received from server');
      } else {
        console.error('[GoAuthApi] Error message:', error.message);
      }
      
      throw error;
    }
  }
  */

  // Caching for subscription check
  static cachedSubscriptionStatus = null; // FIX: null instead of false by default
  static subscriptionChecks = 0;
  static MAX_SUBSCRIPTION_CHECKS = 5;
  static lastSubscriptionCheck = 0;
  static SUBSCRIPTION_CACHE_TIME = 30000; // FIX: Reduced to 30 seconds
  static subscriptionCheckInProgress = false;
  static subscriptionRequestQueue = [];
  static subscriptionRetryAttempted = false;

  // Method for forced subscription cache reset
  static clearSubscriptionCache() {
    console.log('[GoAPI] 🧹 CLEARING subscription cache completely...');
    this.cachedSubscriptionStatus = null; // FIX: null instead of false
    this.lastSubscriptionCheck = 0;
    this.subscriptionChecks = 0;
    this.subscriptionCheckInProgress = false;
    this.subscriptionRetryAttempted = false;
    window.__SUBSCRIPTION_CHECK_IN_PROGRESS = false;
    console.log('[GoAPI] ✅ Subscription cache cleared - forcing fresh check');
  }

  // Improved subscription check with caching and cycle protection
  static async checkSubscription(token) {
    // Increment counter
    this.subscriptionChecks++;

    try {
      // FIX: More strict global flag check
      if (window.__SUBSCRIPTION_CHECK_IN_PROGRESS) {
        console.log(`[GoAPI] ⏳ Global subscription check in progress (check #${this.subscriptionChecks})`);
        // FIX: DON'T return cache if it might be stale
        if (this.cachedSubscriptionStatus && (Date.now() - this.lastSubscriptionCheck < 5000)) {
          console.log(`[GoAPI] Using very recent cached status:`, this.cachedSubscriptionStatus);
          return this.cachedSubscriptionStatus;
        }
        console.log(`[GoAPI] ❌ Cache too old or empty, returning default inactive status`);
        return { isActive: false, isTrialing: false };
      }

      // FIX: More strict progress check
      if (this.subscriptionCheckInProgress) {
        console.log(`[GoAPI] ⏳ Subscription check already in progress (check #${this.subscriptionChecks})`);
        // FIX: Only return cache if it's very fresh
        if (this.cachedSubscriptionStatus && (Date.now() - this.lastSubscriptionCheck < 5000)) {
          console.log(`[GoAPI] Using very recent cached status during check:`, this.cachedSubscriptionStatus);
          return this.cachedSubscriptionStatus;
        }
        console.log(`[GoAPI] ❌ No recent cache available, returning default inactive status`);
        return { isActive: false, isTrialing: false };
      }

      // FIX: More strict cache check
      const now = Date.now();
      const cacheAge = now - this.lastSubscriptionCheck;
      const isCacheValid = this.cachedSubscriptionStatus &&
                          this.lastSubscriptionCheck > 0 &&
                          cacheAge < this.SUBSCRIPTION_CACHE_TIME;

      if (isCacheValid) {
        console.log(`[GoAPI] ✅ Using valid cached subscription status (check #${this.subscriptionChecks})`);
        console.log(`[GoAPI] 📊 Cached status:`, this.cachedSubscriptionStatus);
        console.log(`[GoAPI] ⏰ Cache age: ${cacheAge}ms (limit: ${this.SUBSCRIPTION_CACHE_TIME}ms)`);
        return this.cachedSubscriptionStatus;
      } else if (this.cachedSubscriptionStatus) {
        console.log(`[GoAPI] ⚠️ Cache exists but expired (age: ${cacheAge}ms), will refresh`);
      } else {
        console.log(`[GoAPI] 🆕 No cache available, making fresh request`);
      }
      
      // Limit on number of requests
      if (this.subscriptionChecks > this.MAX_SUBSCRIPTION_CHECKS && this.cachedSubscriptionStatus) {
        console.log(`[GoAPI] Max subscription checks (${this.subscriptionChecks}) exceeded, using cached result:`, this.cachedSubscriptionStatus);
        return this.cachedSubscriptionStatus;
      }
      
      // Set execution flag
      this.subscriptionCheckInProgress = true;
      window.__SUBSCRIPTION_CHECK_IN_PROGRESS = true;
      
      console.log(`[GoAPI] Making subscription check request (check #${this.subscriptionChecks})`);
      console.log(`[GoAPI] Token length: ${token ? token.length : 'no token'}`);
      console.log(`[GoAPI] Request URL: ${getGoApiUrl()}/subscription`);
      
      // FIX: Ensure correct token transmission
      const headers = {};
      if (token) {
        // Make sure token is passed in correct format
        headers['Authorization'] = `Bearer ${token}`;
        headers['Content-Type'] = 'application/json';
        console.log(`[GoAPI] Request headers prepared with Authorization Bearer token`);
        console.log(`[GoAPI] Token prefix: ${token.substring(0, 20)}...`);
      } else {
        console.warn(`[GoAPI] No token provided for subscription check`);
      }
      
      // Add additional headers for compatibility
      headers['X-Requested-With'] = 'XMLHttpRequest';
      headers['Accept'] = 'application/json';

      // Add timeout for request
      const response = await axios.get(`${getGoApiUrl()}/subscription`, {
        headers,
        withCredentials: true, // Important for reading cookie
        timeout: 15000, // 15 seconds timeout
        // Add additional options for debugging
        validateStatus: function (status) {
          // Accept statuses 200-299 and 401 for further processing
          return (status >= 200 && status < 300) || status === 401;
        }
      });

      // Special handling for 401 error
      if (response.status === 401) {
        console.error(`[GoAPI] 401 Unauthorized - token may be invalid or expired`);
        console.error(`[GoAPI] Response data:`, response.data);
        
        // Try to refresh token and retry request
        try {
          console.log(`[GoAPI] Attempting to refresh token and retry subscription check`);
          const freshToken = await this.refreshToken();
          
          if (freshToken && freshToken !== token) {
            console.log(`[GoAPI] Token refreshed, retrying subscription check`);

            // Retry request with new token
            const retryResponse = await axios.get(`${getGoApiUrl()}/subscription`, {
              headers: {
                'Authorization': `Bearer ${freshToken}`,
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json'
              },
              withCredentials: true,
              timeout: 15000,
              validateStatus: function (status) {
                return (status >= 200 && status < 300);
              }
            });
            
            // Update cache and return result
            this.cachedSubscriptionStatus = retryResponse.data || { isActive: false, isTrialing: false };
            this.lastSubscriptionCheck = Date.now();
            
            console.log(`[GoAPI] Subscription check successful after token refresh:`, retryResponse.data);
            return retryResponse.data;
          }
        } catch (refreshError) {
          console.error(`[GoAPI] Token refresh failed:`, refreshError);
        }
        
        // FIX: If token refresh failed, return safe result
        console.log(`[GoAPI] ❌ Token refresh failed, returning safe inactive status`);
        return { isActive: false, isTrialing: false };
      }
      
      // Update cache and timestamp
      this.cachedSubscriptionStatus = response.data || { isActive: false, isTrialing: false };
      this.lastSubscriptionCheck = Date.now();
      
      console.log(`[GoAPI] Subscription check successful (check #${this.subscriptionChecks})`);
      console.log(`[GoAPI] Response status: ${response.status}`);
      console.log(`[GoAPI] Response data:`, response.data);
      console.log(`[GoAPI] Parsed isActive: ${response.data?.isActive}`);
      console.log(`[GoAPI] Parsed isTrialing: ${response.data?.isTrialing}`);
      console.log(`[GoAPI] Cached status:`, this.cachedSubscriptionStatus);
      
      // Notify all waiting
      while (this.subscriptionRequestQueue.length > 0) {
        const resolve = this.subscriptionRequestQueue.shift();
        resolve(response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error(`[GoAPI] Subscription check error (check #${this.subscriptionChecks}):`, error);

      // More detailed error handling
      if (error.response) {
        console.error(`[GoAPI] Error response status: ${error.response.status}`);
        console.error(`[GoAPI] Error response data:`, error.response.data);
        console.error(`[GoAPI] Error response headers:`, error.response.headers);
        
        // Special handling for 401 error
        if (error.response.status === 401) {
          console.error(`[GoAPI] 401 Unauthorized - checking token validity`);
          if (token) {
            console.error(`[GoAPI] Token was provided but rejected by server`);
            console.error(`[GoAPI] Token length: ${token.length}`);
            console.error(`[GoAPI] Token prefix: ${token.substring(0, 20)}...`);
            
            // Try to refresh token and retry request only once
            if (!this.subscriptionRetryAttempted) {
              this.subscriptionRetryAttempted = true;
              
              try {
                console.log(`[GoAPI] Attempting to refresh token due to 401 error`);
                const freshToken = await this.refreshToken();
                
                if (freshToken && freshToken !== token) {
                  console.log(`[GoAPI] Token refreshed, retrying subscription check after 401`);
                  
                  // Reset flags for retry attempt
                  this.subscriptionCheckInProgress = false;
                  window.__SUBSCRIPTION_CHECK_IN_PROGRESS = false;

                  // Recursively call checkSubscription with new token
                  return await this.checkSubscription(freshToken);
                }
              } catch (refreshError) {
                console.error(`[GoAPI] Token refresh after 401 failed:`, refreshError);
              } finally {
                // Reset retry flag after some time
                setTimeout(() => {
                  this.subscriptionRetryAttempted = false;
                }, 60000); // 1 minute
              }
            }
          }
        }
      } else if (error.request) {
        console.error(`[GoAPI] No response received:`, error.request);
      } else {
        console.error(`[GoAPI] Error setting up request:`, error.message);
      }
      
      // If there was a network error, set flag in window
      if (error.message === 'Network Error' || error.code === 'ECONNABORTED') {
        window.__SUBSCRIPTION_CHECK_FAILED_RECENTLY = true;
        window.__SUBSCRIPTION_CHECK_FAILED_TIME = Date.now().toString();
      }
      
      // FIX: Notify all waiting about error with safe result
      while (this.subscriptionRequestQueue.length > 0) {
        const resolve = this.subscriptionRequestQueue.shift();
        resolve({ isActive: false, isTrialing: false });
      }

      // FIX: In case of error ALWAYS return safe result
      console.log(`[GoAPI] ❌ Error occurred, returning safe inactive status (not using potentially stale cache)`);
      return { isActive: false, isTrialing: false };
    } finally {
      // Reset flags
      this.subscriptionCheckInProgress = false;
      window.__SUBSCRIPTION_CHECK_IN_PROGRESS = false;
    }
  }

  // Caching for user profile
  static cachedUserProfile = null;
  static profileChecks = 0;
  static MAX_PROFILE_CHECKS = 5;
  static lastProfileCheck = 0;
  static PROFILE_CACHE_TIME = 300000; // 5 minutes
  static profileCheckInProgress = false;
  static profileRetries = 0;
  static MAX_PROFILE_RETRIES = 3;
  static profileRequestQueue = [];

  // Extract information from JWT token
  static extractUserInfoFromJWT(token) {
    if (!token) return null;
    
    try {
      // Extract payload from JWT
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c =>
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join(''));

      const payload = JSON.parse(jsonPayload);

      // Create minimal profile with data from token
      if (payload && payload.sub) {
        return {
          id: payload.sub,
          email: payload.email || 'unknown',
          username: payload.preferred_username || 'User',
          firstName: payload.given_name || 'Guest',
          lastName: payload.family_name || 'User',
          name: payload.name || 'Guest User',
          isFromJWT: true,
          jwtIssuer: payload.iss,
          expiration: payload.exp,
          created_at: new Date().toISOString()
        };
      }
    } catch (error) {
      console.error('[GoAPI] Error extracting user info from JWT:', error);
    }
    
    return null;
  }

  // Get user profile with cookie support and caching
  static async getUserProfile(token) {
    // Increment counter
    this.profileChecks++;

    try {
      // Check cache
      const now = Date.now();
      if (this.cachedUserProfile && (now - this.lastProfileCheck < this.PROFILE_CACHE_TIME)) {
        console.log(`[GoAPI] Using cached user profile (check #${this.profileChecks})`);
        return this.cachedUserProfile;
      }
      
      // Try to get profile from JWT as fallback before checking flag
      const jwtProfile = this.extractUserInfoFromJWT(token);

      // If check already in progress, return last known result
      if (this.profileCheckInProgress) {
        console.log(`[GoAPI] Profile check already in progress (check #${this.profileChecks}), using cached profile`);

        // Preferentially return cached profile if available
        if (this.cachedUserProfile) {
          return this.cachedUserProfile;
        }
        
        // If we have profile from JWT, return it as intermediate option
        if (jwtProfile) {
          console.log(`[GoAPI] Using profile from JWT as fallback while waiting for profile check`);
          this.cachedUserProfile = jwtProfile; // Save to cache for possible future use
          this.lastProfileCheck = Date.now() - this.PROFILE_CACHE_TIME/2; // Set shorter cache time for JWT profile
          return jwtProfile;
        }

        // Wait a bit to give current check a chance to complete
        await new Promise(resolve => setTimeout(resolve, 200));

        // If cached value is available after waiting, return it
        if (this.cachedUserProfile) {
          return this.cachedUserProfile;
        }
      }
      
      // Limit on number of requests
      if (this.profileChecks > this.MAX_PROFILE_CHECKS) {
        if (this.cachedUserProfile) {
          console.log(`[GoAPI] Max profile checks (${this.profileChecks}) exceeded, using cached profile`);
          return this.cachedUserProfile;
        } else if (jwtProfile) {
          console.log(`[GoAPI] Max profile checks (${this.profileChecks}) exceeded, using JWT profile`);
          this.cachedUserProfile = jwtProfile;
          this.lastProfileCheck = Date.now();
          return jwtProfile;
        }
      }
      
      // Set execution flag
      this.profileCheckInProgress = true;

      // Add detailed diagnostics
      console.log(`[GoAPI] Making user profile request (check #${this.profileChecks})`);
      console.log(`[GoAPI] Token length: ${token ? token.length : 'no token'}`);
      console.log(`[GoAPI] Token prefix: ${token ? token.substring(0, 10) + '...' : 'no token'}`);

      const headers = {};
      // Add token to headers only if explicitly specified
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      try {
        // Add timeout for request
        const response = await Promise.race([
          axios.get(`${getGoApiUrl()}/user/profile`, {
            headers: {
              ...headers,
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'application/json'
            },
            withCredentials: true, // Important for reading cookie
            timeout: 15000, // 15 seconds timeout
            // Add status handling
            validateStatus: function (status) {
              return (status >= 200 && status < 300) || status === 401;
            }
          }),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('User profile request timeout')), 15000)
          )
        ]);
        
        // Special handling for 401 error
        if (response.status === 401) {
          console.error(`[GoAPI] Profile 401 Unauthorized - token may be invalid`);
          console.error(`[GoAPI] Profile response data:`, response.data);

          // If we have JWT profile, use it instead of error
          if (jwtProfile) {
            console.log(`[GoAPI] Using JWT profile as fallback after 401 response`);
            this.cachedUserProfile = jwtProfile;
            this.lastProfileCheck = Date.now();
            return jwtProfile;
          }
          
          throw new Error('Profile request returned 401 Unauthorized');
        }
        
        if (!response || !response.data || !response.data.id) {
          console.error(`[GoAPI] User profile response invalid:`, response.data);

          // If we have JWT profile, use it instead of error
          if (jwtProfile) {
            console.log(`[GoAPI] Using JWT profile as fallback after invalid response`);
            this.cachedUserProfile = jwtProfile;
            this.lastProfileCheck = Date.now();
            return jwtProfile;
          }
          
          throw new Error('Invalid user profile data received');
        }
        
        // Update cache and timestamp
        this.cachedUserProfile = response.data;
        this.lastProfileCheck = Date.now();
        this.profileRetries = 0; // Reset retry counter on success
        
        console.log(`[GoAPI] User profile request successful (check #${this.profileChecks}), user ID: ${response.data.id}`);
        
        return response.data;
      } catch (error) {
        console.error(`[GoAPI] Error fetching user profile (check #${this.profileChecks}):`, error.message || error);

        // Increment retry counter
        this.profileRetries++;

        // If we have profile from JWT, use it on error
        if (jwtProfile) {
          console.log(`[GoAPI] Using JWT profile after fetch error`);
          this.cachedUserProfile = jwtProfile;
          this.lastProfileCheck = Date.now();
          return jwtProfile;
        }
        
        // If cached value exists and retry limit exceeded,
        // return cached value
        if (this.cachedUserProfile && this.profileRetries > this.MAX_PROFILE_RETRIES) {
          console.log(`[GoAPI] Using cached profile after ${this.profileRetries} failed attempts`);
          return this.cachedUserProfile;
        }
        
        // Artificial delay before retry with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, this.profileRetries), 10000);

        // If too many errors, prevent cyclic requests
        if (this.profileRetries <= this.MAX_PROFILE_RETRIES) {
          console.log(`[GoAPI] Will retry profile request after ${delay}ms delay (attempt ${this.profileRetries}/${this.MAX_PROFILE_RETRIES})`);
          await new Promise(resolve => setTimeout(resolve, delay));

          // Retry attempt
          this.profileCheckInProgress = false;
          return this.getUserProfile(token);
        }

        // If we have cached profile, return it as fallback
        if (this.cachedUserProfile) {
          return this.cachedUserProfile;
        }
        
        // As last resort, create minimal profile object
        const fallbackProfile = {
          id: token ? 'auth_user' : 'unknown',
          email: 'unknown',
          username: 'Guest User',
          firstName: 'Guest',
          lastName: 'User',
          name: 'Guest User',
          isFallback: true,
          created_at: new Date().toISOString()
        };
        
        this.cachedUserProfile = fallbackProfile;
        this.lastProfileCheck = Date.now();
        return fallbackProfile;
      }
    } catch (error) {
      console.error(`[GoAPI] Critical error in getUserProfile (check #${this.profileChecks}):`, error.message || error);

      // In case of critical error also try JWT profile
      const jwtProfile = this.extractUserInfoFromJWT(token);
      if (jwtProfile) {
        console.log(`[GoAPI] Using JWT profile after critical error`);
        this.cachedUserProfile = jwtProfile;
        this.lastProfileCheck = Date.now();
        return jwtProfile;
      }
      
      // In case of error return last known result
      if (this.cachedUserProfile) {
        return this.cachedUserProfile;
      }

      // As last resort, create minimal profile object
      const fallbackProfile = {
        id: token ? 'auth_user' : 'unknown',
        email: 'unknown',
        username: 'Guest User',
        firstName: 'Guest',
        lastName: 'User',
        name: 'Guest User',
        isFallback: true,
        created_at: new Date().toISOString()
      };

      this.cachedUserProfile = fallbackProfile;
      this.lastProfileCheck = Date.now();
      return fallbackProfile;
    } finally {
      // Reset flag with small delay
      setTimeout(() => {
        this.profileCheckInProgress = false;
      }, 500);
    }
  }

  // Create Stripe checkout session with cookie support
  static async createCheckout(token, priceId) {
    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      // Add token to headers only if explicitly specified
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.post(`${getGoApiUrl()}/checkout`, { priceId }, {
        headers,
        withCredentials: true // Important for reading cookie
      });
      return response.data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }
}

export default GoAuthApi;